import { getAuthProviderFetchJson } from '../Auth';
import { superAdminApiServer } from '../config';
import { CatalogPlugin, CursorPage, PayPolicyTemplate, PluginSchema, TemplateContent, TemplatePlugin } from '../../Pages/PayPolicyTemplates/types';

export const PAGE_SIZE = 25;

/** The least a search asks for before it runs. */
export const MIN_SEARCH_LENGTH = 3;

const TEMPLATE_SEARCH_RE = new RegExp(`[\\p{L}\\p{N}]{${MIN_SEARCH_LENGTH},}`, 'u');

/**
 * A template search needs that many letters or digits in a row: day-pay-policies searches names
 * through a trigram index, which reads only words, and refuses a search it could not narrow.
 */
export const searchable = (name: string) => TEMPLATE_SEARCH_RE.test(name);

/** Any text of that length runs; the company search keeps its spaces ("ny "), the plugin search trims them, as plugin ids have none. */
export const textSearchable = (text: string) => text.trim() !== '' && text.length >= MIN_SEARCH_LENGTH;

const query = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.set(key, String(value));
  });
  return search.toString();
};

const templateSearch = (name: string) => (searchable(name) ? name.trim() : undefined);
const pluginSearch = (name: string) => (textSearchable(name.trim()) ? name.trim() : undefined);

export interface TemplateFilters {
  /** Templates assigned to any of these companies; global ones are left out. */
  companyUuids?: string[];
  /** Templates listing any of these ISO country codes; ones with no countries are left out. */
  countries?: string[];
}

const joined = (values?: string[]) => (values && values.length > 0 ? values.join(',') : undefined);

export const listTemplates = async (name: string, cursor?: string, filters: TemplateFilters = {}): Promise<CursorPage<PayPolicyTemplate>> => {
  const params = { limit: PAGE_SIZE, cursor, name: templateSearch(name), companyUuid: joined(filters.companyUuids), region: joined(filters.countries) };
  const { json } = await getAuthProviderFetchJson(`${superAdminApiServer}/pay-policy-templates?${query(params)}`);
  return json as CursorPage<PayPolicyTemplate>;
};

export const getTemplate = async (uuid: string): Promise<PayPolicyTemplate> => {
  const { json } = await getAuthProviderFetchJson(`${superAdminApiServer}/pay-policy-templates/${uuid}`);
  return json.content as PayPolicyTemplate;
};

/** Every version of the family this template belongs to, newest first. */
export const listVersions = async (uuid: string): Promise<PayPolicyTemplate[]> => {
  const { json } = await getAuthProviderFetchJson(`${superAdminApiServer}/pay-policy-templates/${uuid}/versions`);
  return json.content as PayPolicyTemplate[];
};

export const createTemplate = async (content: TemplateContent): Promise<PayPolicyTemplate> => {
  const { json } = await getAuthProviderFetchJson(`${superAdminApiServer}/pay-policy-templates`, { method: 'POST', body: JSON.stringify(content) });
  return json.content as PayPolicyTemplate;
};

export interface UpdateResult {
  template: PayPolicyTemplate;
  /** True when the change was saved as a new version because pay policies use the old one. */
  created: boolean;
}

export const updateTemplate = async (uuid: string, content: TemplateContent, createNewOnBusy: boolean): Promise<UpdateResult> => {
  const body = createNewOnBusy ? { ...content, createNewOnBusy } : content;
  const { json } = await getAuthProviderFetchJson(`${superAdminApiServer}/pay-policy-templates/${uuid}`, { method: 'PATCH', body: JSON.stringify(body) });
  return { template: json.content as PayPolicyTemplate, created: Boolean(json.metadata?.created) };
};

export const listPlugins = async (name: string, cursor?: string): Promise<CursorPage<CatalogPlugin>> => {
  const { json } = await getAuthProviderFetchJson(`${superAdminApiServer}/global-calculations/plugins?${query({ limit: PAGE_SIZE, cursor, name: pluginSearch(name) })}`);
  return json as CursorPage<CatalogPlugin>;
};

export const pluginSchemas = async (plugins: TemplatePlugin[]): Promise<PluginSchema[]> => {
  const { json } = await getAuthProviderFetchJson(`${superAdminApiServer}/global-calculations/plugins/schemas`, {
    method: 'POST',
    body: JSON.stringify({ plugins: plugins.map(({ pluginId, version }) => ({ id: pluginId, version })) })
  });
  return json.content.plugins as PluginSchema[];
};

const textOf = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(textOf).filter(Boolean).join('; ') || null;
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return textOf(record.detail) ?? textOf(record.message) ?? textOf(record.title);
  }
  return null;
};

/** What went wrong, readable: upstream problem bodies arrive as JSON inside the message. */
export const describeError = (caught: unknown): string => {
  const raw = caught instanceof Error ? caught.message : String(caught);
  try {
    return textOf(JSON.parse(raw)) ?? raw;
  } catch {
    return textOf((caught as { body?: unknown })?.body) ?? raw;
  }
};

/** The problem type an upstream answered with, e.g. "urn:day-global-calculations:plugin_not_found", or null. */
export const problemType = (caught: unknown): string | null => {
  try {
    const parsed = JSON.parse(caught instanceof Error ? caught.message : String(caught));
    return typeof parsed?.type === 'string' ? parsed.type : null;
  } catch {
    return null;
  }
};

const bodyOf = (caught: unknown): Record<string, unknown> | null => {
  const body = (caught as { body?: unknown })?.body;
  return body && typeof body === 'object' && !Array.isArray(body) ? (body as Record<string, unknown>) : null;
};

/** Why a save was refused, as the editor's dialog shows it. */
export interface SaveRefusal {
  /** What was refused, e.g. "Plugin graph rejected by the calculations service". */
  summary: string;
  /** Why, when the upstream said more than the summary. */
  detail: string | null;
  /** The upstream problem code, e.g. "urn:day-global-calculations:allowed_when_violated". */
  code: string | null;
  /** One line per reason a request schema refusal lists. */
  reasons: string[];
}

const reasonOf = (entry: unknown): string | null => {
  if (!entry || typeof entry !== 'object') return textOf(entry);
  const { message, location } = entry as { message?: unknown; location?: unknown };
  const text = textOf(message);
  if (!text) return null;
  return typeof location === 'string' && location ? `${location}: ${text}` : text;
};

export const describeRefusal = (caught: unknown): SaveRefusal => {
  const body = bodyOf(caught);
  const summary = textOf(body?.message) ?? textOf(body?.title) ?? describeError(caught);
  const detail = textOf(body?.detail);
  const reasons = Array.isArray(body?.errors) ? (body?.errors as unknown[]).map(reasonOf).filter((reason): reason is string => Boolean(reason)) : [];
  return { summary, detail: detail && detail !== summary ? detail : null, code: typeof body?.code === 'string' ? body.code : null, reasons };
};

/** The why of a refusal in one line, for the footer beside the summary: the detail, else the reasons; null when there is neither. */
export const refusalReason = (refusal: SaveRefusal): string | null => refusal.detail ?? (refusal.reasons.length > 0 ? refusal.reasons.join('; ') : null);

/** A 409 that means pay policies use the template, as opposed to a name another template holds. */
export const isTemplateInUse = (caught: unknown): boolean => (caught as { status?: number })?.status === 409 && /referenced by pay policies/i.test(textOf(bodyOf(caught)?.message) ?? describeError(caught));
